import {
  Box,
  Input,
  MenuItem,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { LogInSignUpModal } from '@/components/LogInSignUp';
import { EPrepVersionFilesSource } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useFileUpload } from '@/hooks/useFileUpload';
import { logError } from '@/imports/logging/ClientLogger';
import { PlusIcon } from '@/imports/ui/chakra/feather';

const MAX_NUM_UPLOADS = 7;

type ILibraryUploadInputProps = {
  onUploaded: (options: { versionFileIds: string[] }) => void;
  content?: React.ReactNode;
  // TODO: can remove once we can refactor to passing FileUpload[] to mutation.
  onStartUploads: () => void;
  onEndUploads: () => void;
};

export const LibraryUploadInput: React.FC<ILibraryUploadInputProps> = (
  props
) => {
  const loginDisclosure = useDisclosure();
  const current = useCurrent();
  const toast = useToast();
  const ref = React.useRef<HTMLInputElement>(null);
  const { uploadFiles } = useFileUpload(EPrepVersionFilesSource.LIBRARY);

  return current.loading ? null : (
    <Box>
      <Input
        type="file"
        multiple={true}
        onChange={async (event) => {
          props.onStartUploads();
          const files = event.currentTarget.files
            ? Array.from(event.currentTarget.files)
            : [];
          if (files.length > MAX_NUM_UPLOADS) {
            toast({
              status: 'error',
              title: 'Reminder',
              description: `Unable to upload more than ${MAX_NUM_UPLOADS} files at a time.`,
            });
            return;
          }
          try {
            const promisedResources = files.map(async (file) => {
              const resource = await uploadFiles([file]);

              return resource
                ? { versionFileId: resource[0], fileName: file.name }
                : null;
            });
            const versionFileIds = (await Promise.all(promisedResources))
              .filter(
                (
                  resource
                ): resource is { versionFileId: string; fileName: string } =>
                  !!resource
              )
              .map((resource) => resource.versionFileId);

            props.onUploaded({ versionFileIds });
          } catch (e) {
            toast({ status: 'error', description: 'Failed to upload.' });
            logError(e);
          } finally {
            props.onEndUploads();
          }
        }}
        ref={ref}
        display="none"
      />
      <MenuItem
        icon={<PlusIcon color="gray.600" />}
        onClick={() => {
          if (!current.user) {
            loginDisclosure.onOpen();
          } else {
            ref.current?.click();
          }
        }}
      >
        Add Files
      </MenuItem>
      <LogInSignUpModal
        isOpen={loginDisclosure.isOpen}
        onClose={loginDisclosure.onClose}
      />
    </Box>
  );
};
